/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { Fragment, useCallback, useEffect, useState } from 'react';

import { jsx } from '@atlaskit/css';
import { UNSAFE_Ribbon as RibbonSlot } from '@atlaskit/navigation-system/experimental/ribbon';
import { ERROR_LOCATION__RIBBON, NavErrorBoundary } from '@atlassian/navigation-error-boundary';

import type { ProductId } from '../../common/ui/app-tile/app-selector/types';
import { ReorderableAppTile } from '../../common/ui/drag-and-drop/reorderable-app-tile';
import {
	KeyboardReorderProvider,
	useKeyboardReorderContext,
} from '../../controllers/keyboard-reorder-provider';
import { RibbonConfigProvider } from '../../controllers/ribbon-config-provider';
import { useDragAndDrop } from '../../controllers/use-drag-and-drop';
import type { AppTileConfig, NavigationRibbonProps } from '../../types';

import { RIBBON_WIDTH } from './constants';
import { ErrorState } from './error-state';
import { MoreAppsPopup } from './more-apps/popup';
import { OrgSwitcherPopup } from './org-switcher/popup';
import RibbonLayout from './ribbon-layout';

// TODO: remove mocks and accept props to populate tiles
const DEFAULT_SELECTED_APP: ProductId = 'JIRA';
const DEFAULT_APPS: AppTileConfig[] = [
	{ productId: 'JIRA', href: '#', title: 'JIRA' },
	{ productId: 'CONFLUENCE', href: '#', title: 'Confluence' },
	{ productId: 'TRELLO', href: '#', title: 'Trello' },
];
const DEFAULT_TEST_ID = 'navigation-ribbon';

function NavigationRibbonContent({
	testId,
	apps,
	onAppsReorder,
	selectedApp = DEFAULT_SELECTED_APP,
}: NavigationRibbonProps) {
	// Store both visibility states in the same state object to prevent race conditions
	// that result in both popups being visible at the same time.
	const [popupVisibility, setPopupVisibility] = useState({
		isMoreAppsVisible: false,
		isOrgSwitcherVisible: false,
	});

	const providedApps = apps ?? DEFAULT_APPS;

	const [uncontrolledApps, setUncontrolledApps] = useState<AppTileConfig[]>(() => providedApps);

	// Keep uncontrolled state in sync with prop changes (if used).
	useEffect(() => {
		if (onAppsReorder) {
			return;
		}

		// When uncontrolled, treat `apps` prop as an upstream reset.
		setUncontrolledApps(providedApps);
	}, [onAppsReorder, providedApps]);

	const appTiles = onAppsReorder ? providedApps : uncontrolledApps;

	const updateAppTiles = useCallback(
		(next: AppTileConfig[]) => {
			if (onAppsReorder) {
				// Controlled usage: delegate persistence/ordering to the consumer.
				onAppsReorder(next);
			} else {
				// Uncontrolled usage: keep state local (storybook + local demos).
				setUncontrolledApps(next);
			}
		},
		[onAppsReorder],
	);

	const {
		isKeyboardReorderModeEnabled,
		temporaryKeyboardOrderedAppTiles,
		setAppTiles,
		setOnAppsReorder,
	} = useKeyboardReorderContext();

	useEffect(() => {
		setAppTiles(appTiles);
	}, [appTiles, setAppTiles]);

	useEffect(() => {
		setOnAppsReorder(() => updateAppTiles);
	}, [setOnAppsReorder, updateAppTiles]);

	const { draggingProductId, isDragActive } = useDragAndDrop({
		apps: appTiles,
		onAppsReorder: updateAppTiles,
	});

	const testIdPrefix = testId ?? DEFAULT_TEST_ID;

	return (
		<Fragment>
			<RibbonSlot width={RIBBON_WIDTH}>
				<RibbonLayout
					testId={testIdPrefix}
					header={
						<OrgSwitcherPopup
							isOpen={popupVisibility.isOrgSwitcherVisible}
							onClose={() =>
								setPopupVisibility((prev) => ({
									...prev,
									isOrgSwitcherVisible: false,
								}))
							}
							onOpen={() =>
								setPopupVisibility((prev) => ({
									isMoreAppsVisible: false,
									isOrgSwitcherVisible: !prev.isOrgSwitcherVisible,
								}))
							}
						/>
					}
					content={(isKeyboardReorderModeEnabled ? temporaryKeyboardOrderedAppTiles : appTiles).map(
						(app, index) => (
							<ReorderableAppTile
								key={app.productId}
								app={app}
								index={index}
								selectedApp={selectedApp}
								isDragging={draggingProductId === app.productId}
								testId={testIdPrefix}
							/>
						),
					)}
					footer={
						<MoreAppsPopup
							appTiles={appTiles}
							isDragActive={isDragActive}
							isOpen={popupVisibility.isMoreAppsVisible}
							onClose={() =>
								setPopupVisibility((prev) => ({
									...prev,
									isMoreAppsVisible: false,
								}))
							}
							onOpen={() =>
								setPopupVisibility((prev) => ({
									isOrgSwitcherVisible: false,
									isMoreAppsVisible: !prev.isMoreAppsVisible,
								}))
							}
							testIdPrefix={testIdPrefix}
						/>
					}
				/>
			</RibbonSlot>
		</Fragment>
	);
}

/**
 * Navigation Ribbon component.
 *
 * - Renders a vertical list of app tiles.
 * - Supports mouse-driven drag-and-drop reordering using Pragmatic DnD.
 * - While dragging, the footer flips from "More Apps" to a "Hide" target.
 *
 * Persistence:
 * - If `onAppsReorder` is provided, the ribbon is controlled and emits updates.
 * - Otherwise, the ribbon manages app order in-memory.

 */
export default function NavigationRibbon(props: NavigationRibbonProps): JSX.Element {
	const { product = 'jira' } = props;

	return (
		<RibbonConfigProvider product={product}>
			<NavErrorBoundary
				errorFallbackUI={({ header, description }) => (
					<RibbonLayout content={<ErrorState header={header} description={description} />} />
				)}
				location={ERROR_LOCATION__RIBBON}
				sentryConfig={{ product }}
			>
				<KeyboardReorderProvider>
					<NavigationRibbonContent {...props} />
				</KeyboardReorderProvider>
			</NavErrorBoundary>
		</RibbonConfigProvider>
	);
}
